// src/pages/ProductList.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getAllProducts, deleteProduct, getAllCategories, getEstabelecimentos } from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import { normalizeSearchTerm } from '../utils/productSearch';
import BuscaProdutoCampo from './common/BuscaProdutoCampo';
import ProductFormDialog from './ProductFormDialog';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import SortableTh from './common/SortableTh';
import { StatusAtivoChip, sxLinhaInativa } from './common/StatusAtivo';
import { useServerSort } from '../hooks/useServerSort';
import {
  Box, Typography, Button, Table, TableBody, TableCell,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Snackbar, Alert, Checkbox, Pagination,
  Stack, CircularProgress, FormControl, InputLabel, Select, MenuItem, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useEstabelecimento } from '../context/EstabelecimentoContext';

function ProductList() {
  const { estabelecimentoId: filialContexto } = useEstabelecimento();
  // paginação server-side
  const [pagina, setPagina] = useState(1);      // 1-based
  const [itensPorPagina] = useState(20);        // padrão 20

  // dados da API
  const [produtos, setProdutos] = useState([]);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalItens, setTotalItens] = useState(0);

  // filtros
  const [categorias, setCategorias] = useState([]);
  const [categoriaSelecionada, setCategoriaSelecionada] = useState('');
  const [ativoFiltro, setAtivoFiltro] = useState('true'); // '' todos | 'true' | 'false'
  const [filiais, setFiliais] = useState([]);
  const [filialEstoqueId, setFilialEstoqueId] = useState('');

  // busca: texto digitado vs termo realmente enviado
  const [searchText, setSearchText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // UI
  const [dialogoAberto, setDialogoAberto] = useState(false);
  const [idsParaExcluir, setIdsParaExcluir] = useState([]);
  const [mensagemSnackbar, setMensagemSnackbar] = useState('');
  const [snackbarAberto, setSnackbarAberto] = useState(false);
  const [severidadeAlerta, setSeveridadeAlerta] = useState('success');
  const [produtosSelecionados, setProdutosSelecionados] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [formProduto, setFormProduto] = useState({ open: false, productId: null, duplicateFromId: null });

  // formatação de moeda
  const moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

  // helper: extrai preço pelo tipo (ajuste os códigos se necessário)
  // Assumido: 0 = Varejo, 3 = Custo
  const getPrecoPorTipo = (produto, tipo) => {
    const p = (produto?.precos || []).find(x => Number(x.tipo) === Number(tipo));
    return p ? moeda.format(p.valor) : '-';
  };

  // carregar categorias
  useEffect(() => {
    (async () => {
      try {
        const respostaCategorias = await getAllCategories({ indice: 1, qtde: 200 });
        setCategorias(respostaCategorias.data.items || []);
      } catch {
        // silencioso
      }
      try {
        const { data } = await getEstabelecimentos();
        const lista = Array.isArray(data) ? data : (data ? [data] : []);
        setFiliais(lista);
        if (lista.length === 1) setFilialEstoqueId(String(lista[0].id));
      } catch {
        // silencioso
      }
    })();
  }, []);

  useEffect(() => {
    if (filialContexto) {
      setFilialEstoqueId(String(filialContexto));
    }
  }, [filialContexto]);

  const { orderBy, order, requestSort: aplicarOrdem } = useServerSort('descricao', 'asc');
  const requestSort = (key) => {
    setPagina(1);
    aplicarOrdem(key);
  };

  // requisita produtos (paginado + filtros + busca confirmada)
  const carregarPagina = useCallback(async () => {
    try {
      setCarregando(true);
      const resp = await getAllProducts({
        indice: pagina,
        qtde: itensPorPagina,
        termo: normalizeSearchTerm(searchTerm),
        categoriaId: categoriaSelecionada,
        ativo: ativoFiltro,
        estabelecimentoId: filialEstoqueId || '',
        ordenar: orderBy,
        direcao: order,
      });
      const data = resp.data;
      setProdutos(data.items || []);
      setTotalPaginas(data.totalPages || 1);
      setTotalItens(data.totalItems || 0);
      setProdutosSelecionados([]);
    } catch (erro) {
      setMensagemSnackbar('Erro ao carregar produtos');
      setSeveridadeAlerta('error');
      setSnackbarAberto(true);
    } finally {
      setCarregando(false);
    }
  }, [pagina, itensPorPagina, searchTerm, categoriaSelecionada, ativoFiltro, filialEstoqueId, orderBy, order]);

  useEffect(() => {
    carregarPagina();
  }, [carregarPagina]);

  // ao trocar categoria/status, volta pra página 1 (e a effect recarrega)
  useEffect(() => {
    setPagina(1);
  }, [categoriaSelecionada, ativoFiltro, filialEstoqueId]);

  const acionarBusca = () => {
    setPagina(1);
    setSearchTerm(searchText);
  };

  // seleção — a ordem já veio do servidor
  const produtosPaginaAtual = produtos;
  const produtosOrdenados = produtosPaginaAtual;
  const idsNaPagina = useMemo(
    () => produtosOrdenados.map((p) => p.id).filter(Boolean),
    [produtosOrdenados],
  );
  const navIdx = formProduto.productId
    ? idsNaPagina.indexOf(formProduto.productId)
    : -1;
  const selecionarTodos = (e) => {
    setProdutosSelecionados(e.target.checked ? produtosPaginaAtual.map(p => p.id) : []);
  };
  const selecionarProduto = (id) => {
    setProdutosSelecionados(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // diálogo de exclusão
  const abrirDialogo = (ids) => {
    setIdsParaExcluir(Array.isArray(ids) ? ids : [ids]);
    setDialogoAberto(true);
  };
  const fecharDialogo = () => {
    setDialogoAberto(false);
    setIdsParaExcluir([]);
  };

  // exclusão
  const excluirProdutos = async () => {
    if (idsParaExcluir.length === 0) return;
    try {
      await Promise.all(idsParaExcluir.map(id => deleteProduct(id)));
      setMensagemSnackbar(
        idsParaExcluir.length > 1
          ? `${idsParaExcluir.length} produtos excluídos com sucesso!`
          : 'Produto excluído com sucesso!'
      );
      setSeveridadeAlerta('success');
      await carregarPagina();
      if (produtosPaginaAtual.length === idsParaExcluir.length && pagina > 1) {
        setPagina(pagina - 1);
      }
    } catch (erro) {
      setMensagemSnackbar(getApiErrorMessage(erro, 'Erro ao excluir o(s) produto(s).'));
      setSeveridadeAlerta('error');
    } finally {
      setSnackbarAberto(true);
      fecharDialogo();
    }
  };

  // ficha do produto em janela flutuante
  const fecharSnackbar = () => setSnackbarAberto(false);
  const editarProduto = (id) => setFormProduto({ open: true, productId: id, duplicateFromId: null });
  const criarProduto = () => setFormProduto({ open: true, productId: null, duplicateFromId: null });
  const duplicarProduto = (id) => setFormProduto({ open: true, productId: null, duplicateFromId: id });
  const fecharFormProduto = () => setFormProduto((s) => ({ ...s, open: false }));
  const aoSalvarProduto = async (msg) => {
    fecharFormProduto();
    setMensagemSnackbar(msg || 'Produto salvo');
    setSeveridadeAlerta('success');
    setSnackbarAberto(true);
    await carregarPagina();
  };
  const mudarPagina = (_e, novaPagina) => setPagina(novaPagina);

  // cabeçalho checkbox
  const todosSelecionados = useMemo(
    () => produtosPaginaAtual.length > 0 && produtosSelecionados.length === produtosPaginaAtual.length,
    [produtosPaginaAtual, produtosSelecionados]
  );
  const indeterminate = produtosSelecionados.length > 0 && !todosSelecionados;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Produtos"
        subtitle="Cadastro de produtos sincronizado com os PDVs."
        actions={
          <>
            {produtosSelecionados.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => abrirDialogo(produtosSelecionados)}
              >
                Excluir selecionados ({produtosSelecionados.length})
              </Button>
            )}
            <Button variant="contained" startIcon={<AddIcon />} onClick={criarProduto}>
              Novo produto
            </Button>
          </>
        }
      />

      {/* Filtros */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <BuscaProdutoCampo
          value={searchText}
          onChange={setSearchText}
          onSearch={acionarBusca}
        />
        <Button variant="outlined" onClick={acionarBusca}>Buscar</Button>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="filtro-categoria-label">Categoria</InputLabel>
          <Select
            labelId="filtro-categoria-label"
            value={categoriaSelecionada}
            label="Categoria"
            onChange={(e) => setCategoriaSelecionada(e.target.value)}
          >
            <MenuItem value=""><em>Todas</em></MenuItem>
            {categorias.map(c => (
              <MenuItem key={c.id} value={String(c.id)}>{c.descricao}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="filtro-ativo-label">Status</InputLabel>
          <Select
            labelId="filtro-ativo-label"
            value={ativoFiltro}
            label="Status"
            onChange={(e) => setAtivoFiltro(e.target.value)}
          >
            <MenuItem value=""><em>Todos</em></MenuItem>
            <MenuItem value="true">Ativos</MenuItem>
            <MenuItem value="false">Inativos</MenuItem>
          </Select>
        </FormControl>

        {filiais.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="filtro-filial-label">Estoque da filial</InputLabel>
            <Select
              labelId="filtro-filial-label"
              value={filialEstoqueId}
              label="Estoque da filial"
              onChange={(e) => setFilialEstoqueId(e.target.value)}
            >
              <MenuItem value=""><em>Total (todas)</em></MenuItem>
              {filiais.map((f) => (
                <MenuItem key={f.id} value={String(f.id)}>
                  {f.nomeFantasia || f.razaoSocial || `Filial ${f.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Paper>

      {/* Tabela */}
      <ResponsiveTable>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox indeterminate={indeterminate} checked={todosSelecionados} onChange={selecionarTodos} />
              </TableCell>
              <SortableTh id="descricao" label="Descrição" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="codigo" label="Código" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="ean" label="EAN" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="referencia" label="Referência" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="categoria" label="Categoria" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="varejo" label="Preço Varejo" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="custo" label="Preço Custo" align="right" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="status" label="Status" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="estoque" label="Estoque" orderBy={orderBy} order={order} onSort={requestSort} />
              <TableCell align="right"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {carregando ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell>
              </TableRow>
            ) : produtosOrdenados.length > 0 ? (
              produtosOrdenados.map((produto) => (
                <TableRow
                  key={produto.id}
                  hover
                  sx={{
                    cursor: 'pointer',
                    ...sxLinhaInativa(produto.ativo),
                  }}
                  onClick={() => editarProduto(produto.id)}
                >
                  <TableCell padding="checkbox" data-label="Selecionar" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={produtosSelecionados.includes(produto.id)}
                      onChange={() => selecionarProduto(produto.id)}
                    />
                  </TableCell>
                  <TableCell data-label="Descrição">{produto.descricao}</TableCell>
                  <TableCell data-label="Código">{produto.codigo || '-'}</TableCell>
                  <TableCell data-label="EAN">{produto.ean || '-'}</TableCell>
                  <TableCell data-label="Referência">{produto.referencia || '-'}</TableCell>
                  <TableCell data-label="Categoria">{produto.categoriaNome || 'Sem Categoria'}</TableCell>
                  <TableCell align="right" data-label="Preço varejo">{getPrecoPorTipo(produto, 0)}</TableCell> {/* Varejo */}
                  <TableCell align="right" data-label="Preço custo">{getPrecoPorTipo(produto, 3)}</TableCell> {/* Custo */}
                  <TableCell data-label="Status">
                    <StatusAtivoChip ativo={produto.ativo} />
                  </TableCell>
                  <TableCell align="left" data-label="Estoque">{produto.estoqueAtual}</TableCell>
                  <TableCell align="right" data-actions="" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Editar">
                      <IconButton color="primary" onClick={() => editarProduto(produto.id)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicar">
                      <IconButton color="secondary" onClick={() => duplicarProduto(produto.id)}>
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton color="error" onClick={() => abrirDialogo(produto.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhum produto encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTable>

      {/* Paginação */}
      <Stack spacing={1} sx={{ mt: 2, alignItems: 'center' }}>
        <Pagination count={totalPaginas} page={pagina} onChange={mudarPagina} color="primary" />
        <Typography variant="body2" color="text.secondary">
          Exibindo {produtosPaginaAtual.length} de {totalItens} produtos (página {pagina}/{totalPaginas})
        </Typography>
      </Stack>

      <ProductFormDialog
        open={formProduto.open}
        productId={formProduto.productId}
        duplicateFromId={formProduto.duplicateFromId}
        onClose={fecharFormProduto}
        onSaved={aoSalvarProduto}
        onPrev={
          navIdx > 0
            ? () =>
                setFormProduto((s) => ({
                  ...s,
                  productId: idsNaPagina[navIdx - 1],
                  duplicateFromId: null,
                }))
            : null
        }
        onNext={
          navIdx >= 0 && navIdx < idsNaPagina.length - 1
            ? () =>
                setFormProduto((s) => ({
                  ...s,
                  productId: idsNaPagina[navIdx + 1],
                  duplicateFromId: null,
                }))
            : null
        }
        navLabel={
          navIdx >= 0
            ? `${navIdx + 1} / ${idsNaPagina.length} nesta página`
            : null
        }
      />

      {/* Diálogo de confirmação */}
      <Dialog open={dialogoAberto} onClose={fecharDialogo}>
        <DialogTitle>{idsParaExcluir.length > 1 ? 'Excluir Produtos' : 'Excluir Produto'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {idsParaExcluir.length > 1
              ? `Tem certeza que deseja excluir os ${idsParaExcluir.length} produtos selecionados? Esta ação não pode ser desfeita.`
              : 'Tem certeza que deseja excluir este produto? Esta ação não pode ser desfeita.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharDialogo} color="inherit">Cancelar</Button>
          <Button onClick={excluirProdutos} color="error" autoFocus>Excluir</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbarAberto} autoHideDuration={6000} onClose={fecharSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={fecharSnackbar} severity={severidadeAlerta} sx={{ width: '100%' }}>
          {mensagemSnackbar}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ProductList;
